import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { hortonGuidanceService } from '../../services/hortonGuidanceService'
import {
  Settings,
  X,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Smartphone,
  FlaskConical,
  Inbox,
} from 'lucide-react'
import { useTheme } from '../providers/ThemeProvider'
import { useBranding } from '../../hooks/useBranding'
import { useTimeOfDay } from '../../hooks/useTimeOfDay'
import { useRailPresence } from '../../hooks/useRailPresence'

import {
  navigationGroups,
  rooms,
  utilityItems,
  type NavGroup,
  type RoomItem,
} from './navItems'

interface SidebarProps {
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  mobileSidebarOpen: boolean
  setMobileSidebarOpen: (open: boolean) => void
}

/** Each room's hue, written out as literal class names. Tailwind reads source
 *  text, so a computed `text-room-${hue}` would compile to nothing. The
 *  values themselves live in theme.css as --room-*.
 *
 *  `ink` is the -ink token and NOT the bare hue: the hue is only legible as
 *  text on a light page. On a dark theme it made the active room read dimmer
 *  than its inactive neighbours. See the note in theme.css. */
const HUE: Record<
  RoomItem['hue'],
  { ink: string; fill: string; edge: string; rule: string; on: string }
> = {
  today: {
    ink: 'text-room-today-ink',
    fill: 'bg-room-today-fill',
    edge: 'border-room-today-edge',
    rule: 'bg-room-today',
    on: 'bg-room-today',
  },
  map: {
    ink: 'text-room-map-ink',
    fill: 'bg-room-map-fill',
    edge: 'border-room-map-edge',
    rule: 'bg-room-map',
    on: 'bg-room-map',
  },
  compass: {
    ink: 'text-room-compass-ink',
    fill: 'bg-room-compass-fill',
    edge: 'border-room-compass-edge',
    rule: 'bg-room-compass',
    on: 'bg-room-compass',
  },
}

export function Sidebar({ sidebarOpen, setSidebarOpen, mobileSidebarOpen, setMobileSidebarOpen }: SidebarProps) {
  const location = useLocation()
  const { isDiary, diaryFontFamily } = useTheme()
  const branding = useBranding()
  const sky = useTimeOfDay()
  const presence = useRailPresence()
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    navigationGroups.forEach(group => {
      if (group.defaultOpen) initial.add(group.name)
    })
    return initial
  })

  // Live "unread" count for the yanamas' Inbox nav badge. Excludes the active
  // conversation's held items (they live in the chat, not the Inbox). Refreshes
  // on navigation and on a gentle interval.
  const [inboxCount, setInboxCount] = useState(0)
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const activeChatId = sessionStorage.getItem('horton_active_chat_id') || undefined
        const inbox = await hortonGuidanceService.getInbox(12, activeChatId)
        if (!cancelled) {
          setInboxCount((inbox.threads?.length || 0) + (inbox.world_candidates?.length || 0))
        }
      } catch {
        // Leave the last known count in place (2026-08-29): forcing 0 made a
        // transient failure erase a badge that was correct a moment earlier.
      }
    }
    void load()
    const id = window.setInterval(load, 30000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [location.pathname])

  // Lab entries are operator tools — shown only to admins (Three Rooms
  // shell, 2026-08-25). One check per mount; fails closed to hidden.
  const [isAdmin, setIsAdmin] = useState(false)
  useEffect(() => {
    let cancelled = false
    fetch('/api/admin/check', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.success) setIsAdmin(Boolean(d.is_admin)) })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [])

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen)
  }

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  const isActive = (href: string) => {
    return location.pathname === href || (href !== '/dashboard' && location.pathname.startsWith(href))
  }

  // Check if any item in a group is active
  const isGroupActive = (group: NavGroup) => {
    return group.items.some(item => isActive(item.href))
  }

  const onPath = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + '/')

  // Diary theme inline styles - Journal spine / book cover aesthetic
  const diaryStyles = {
    header: {
      borderBottom: '2px solid var(--paper-margin)',
      background: 'linear-gradient(180deg, var(--paper-cream) 0%, var(--paper-aged) 100%)',
    } as React.CSSProperties,
  }

  // ── Sky cap ──────────────────────────────────────────────────────────────
  // The ambient header. Everything visual comes from --sky-* tokens that
  // [data-sky] re-points four times a day, so this markup is identical at
  // dawn and midnight.
  const renderSkyCap = (closeButton: React.ReactNode) => (
    <div
      data-sky={sky.phase}
      className="sky-cap relative flex-none overflow-hidden"
      aria-label={`${sky.greeting}. It is ${sky.phase}.`}
    >
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 256 128"
        aria-hidden="true"
      >
        <path
          d="M18 104 Q128 30 238 104"
          fill="none"
          stroke="var(--sky-arc)"
          strokeWidth="1.5"
          strokeDasharray="2 5"
        />
        {sky.phase === 'night' && (
          <g fill="var(--sky-orb)">
            <circle cx="46" cy="30" r="1.3" opacity="0.75" />
            <circle cx="88" cy="19" r="1" opacity="0.55" />
            <circle cx="150" cy="27" r="1.4" opacity="0.8" />
            <circle cx="196" cy="18" r="1" opacity="0.5" />
            <circle cx="122" cy="47" r="1.1" opacity="0.6" />
          </g>
        )}
      </svg>
      <span className="sky-orb pointer-events-none" aria-hidden="true" />

      <div className="relative flex items-start justify-between gap-2 px-4 pt-3">
        <Link to="/today" className="flex min-w-0 items-center gap-2.5">
          <img src={branding.icon} alt="" className="h-7 w-7 shrink-0 rounded-lg" />
          <span className="truncate font-display text-base font-bold text-sky-lockup">
            {branding.name}
          </span>
        </Link>
        {closeButton}
      </div>

      <div className="absolute inset-x-4 bottom-3">
        <div className="truncate text-sm font-semibold text-sky-ink">
          {presence.name ? `${sky.greeting}, ${presence.name}` : sky.greeting}
        </div>
        <div className="truncate text-app-micro text-sky-ink-soft">{sky.line}</div>
      </div>
    </div>
  )

  // ── Rooms, unfolded ──────────────────────────────────────────────────────
  // Children render whether or not their room is active. This is the whole
  // point of the reorg: you can see that Dreams exists without first
  // guessing your way into Life Map.
  const renderRooms = (onItemClick?: () => void) => {
    const activeRoom = rooms.find((r) => r.match.some(onPath))
    return (
      <nav className="min-h-0 flex-1 overflow-y-auto px-3 pt-3" aria-label="Main navigation">
        {rooms.map((room) => {
          const hue = HUE[room.hue]
          const active = activeRoom?.name === room.name
          return (
            <div key={room.name} className="mb-1.5">
              <Link
                to={room.href}
                onClick={onItemClick}
                aria-current={active ? 'page' : undefined}
                className={`relative flex items-center gap-3 rounded-2xl border px-3 py-2.5 transition-colors ${
                  active ? `${hue.fill} ${hue.edge}` : 'border-transparent hover:bg-surface-alt'
                }`}
              >
                {active && (
                  <span
                    className={`absolute inset-y-2.5 left-0 w-1 rounded-r-full ${hue.rule}`}
                    aria-hidden="true"
                  />
                )}
                <room.icon className={`h-5 w-5 shrink-0 ${active ? hue.ink : 'text-secondary'}`} />
                <span className="min-w-0">
                  <span className={`block truncate text-sm font-semibold ${active ? hue.ink : 'text-primary'}`}>
                    {room.name}
                  </span>
                  <span className="block truncate text-app-nano text-muted">{room.sub}</span>
                </span>
              </Link>

              {room.children && (
                <div className={`ml-5 mt-0.5 border-l pl-3 ${hue.edge}`}>
                  {room.children.map((child) => {
                    const childActive = onPath(child.href)
                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        onClick={onItemClick}
                        aria-current={childActive ? 'page' : undefined}
                        className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-app-meta transition-colors ${
                          childActive
                            ? `${hue.on} font-semibold text-on-brand`
                            : 'text-secondary hover:bg-surface-alt'
                        }`}
                      >
                        <child.icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{child.name}</span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}

        {/* the assistant' Inbox: furniture only when it has something to say. */}
        {inboxCount > 0 && (
          <Link
            to="/notifications?tab=decide"
            onClick={onItemClick}
            className="mt-2 flex items-center gap-2.5 rounded-xl border border-tint-warn-edge bg-tint-warn-soft px-3 py-2.5 transition-colors hover:bg-tint-warn-fill"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-warn text-app-nano font-bold text-white">
              {inboxCount}
            </span>
            <span className="truncate text-app-meta text-secondary">
              {branding.assistant} has {inboxCount === 1 ? 'a thing' : `${inboxCount} things`} to ask
            </span>
            <Inbox className="ml-auto h-3.5 w-3.5 shrink-0 text-warn" />
          </Link>
        )}

        {/* Plan & reflect — a label instead of a hard rule, so the rail has
            one fewer line in it. */}
        <div className="px-3 pb-1 pt-4 text-app-nano font-bold uppercase tracking-widest text-muted">
          Plan &amp; reflect
        </div>
        {utilityItems.map((item) => {
          const active = location.pathname === item.href
            || (item.href === '/schedule' && (isActive('/schedule') || isActive('/schedule-v2')))
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onItemClick}
              aria-current={active ? 'page' : undefined}
              className={`flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-app-meta transition-colors ${
                active ? 'bg-brand-subtle font-semibold text-brand' : 'text-secondary hover:bg-surface-alt'
              }`}
            >
              <item.icon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{item.name}</span>
            </Link>
          )
        })}
        {/* The Auto-Mom Log is a DEV/RIG surface (owner ask 2026-08-30):
            its route is only registered when import.meta.env.DEV, so on
            production this link would land on the 404 page. Admin alone
            is no longer enough to show it. */}
        {isAdmin && import.meta.env.DEV && (
          <Link
            to="/dev/automom-activity-log"
            onClick={onItemClick}
            className={`flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-app-meta transition-colors ${
              location.pathname.startsWith('/dev/')
                ? 'bg-brand-subtle font-semibold text-brand'
                : 'text-secondary hover:bg-surface-alt'
            }`}
          >
            <FlaskConical className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Lab</span>
          </Link>
        )}
      </nav>
    )
  }

  // ── The quiet block ──────────────────────────────────────────────────────
  // What used to be ~250px of nothing at the bottom of the rail. It says a
  // line the user wrote themselves, or — only for someone with no dreams at
  // all — an invitation to write the first one, or nothing. It never fills the
  // space with a stock quote, and index.css hides it entirely on short windows.

  // ── Presence footer ──────────────────────────────────────────────────────
  // Absorbs what used to be two full-width rows ("Settings", "Get the App")
  // into one row that also says who is logged in.
  const renderFooter = (onItemClick?: () => void) => (
    <div
      className="flex flex-none items-center gap-2.5 border-t border-soft px-3 py-2.5"
    >
      <Link
        to="/profile"
        onClick={onItemClick}
        className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg py-0.5 transition-colors hover:bg-surface-alt"
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-app-nano font-bold text-brand"
          aria-hidden="true"
        >
          {presence.initial || <Settings className="h-3.5 w-3.5" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-app-meta font-semibold text-primary">
            {presence.name || 'Your account'}
          </span>
          <span className="block truncate text-app-nano text-muted">Settings</span>
        </span>
      </Link>
      <Link
        to="/download"
        onClick={onItemClick}
        title="Get the app"
        aria-label="Get the app"
        className="inline-flex shrink-0 items-center justify-center min-h-touch min-w-touch rounded-lg text-muted transition-colors hover:bg-surface-alt hover:text-secondary"
      >
        <Smartphone className="h-4 w-4" />
      </Link>
    </div>
  )

  const renderNavigation = (onItemClick?: () => void) =>
    isDiary ? renderGroupedNavigation(onItemClick) : renderRooms(onItemClick)

  const renderGroupedNavigation = (onItemClick?: () => void) => (
    <nav
      className="flex-1 px-3 py-4 space-y-1 overflow-y-auto"
      style={isDiary ? { fontFamily: 'var(--font-ui)' } : undefined}
    >
      {/* Diary theme: Table of Contents header */}
      {isDiary && (
        <div 
          className="px-3 py-2 mb-4 text-center border-b-2"
          style={{ 
            borderColor: 'var(--ink-faded)', 
            borderStyle: 'dashed',
          }}
        >
          <span 
            className="text-sm tracking-widest uppercase"
            style={{ 
              color: 'var(--ink-sepia)',
              fontFamily: diaryFontFamily,
            }}
          >
            ✦ Contents ✦
          </span>
        </div>
      )}
      
      {navigationGroups.map((group, groupIndex) => {
        const isExpanded = expandedGroups.has(group.name)
        const groupActive = isGroupActive(group)
        
        // Diary theme styles for group header - Chapter tab style
        const diaryGroupStyle: React.CSSProperties = groupActive
          ? { 
              color: 'var(--ink-sepia)', 
              backgroundColor: 'var(--paper-cream)',
              fontFamily: diaryFontFamily,
              borderLeft: '4px solid var(--bookmark-red)',
              marginLeft: '-4px',
              paddingLeft: 'calc(0.75rem + 4px)',
            }
          : { 
              color: 'var(--ink-faded)',
              fontFamily: 'var(--font-ui)',
            }
        
        return (
          <div key={group.name} className="mb-3">
            {/* Group Header - Chapter Tab */}
            <button
              onClick={() => toggleGroup(group.name)}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-r-lg transition-all ${
                !isDiary 
                  ? groupActive 
                    ? 'text-brand bg-brand-subtle' 
                    : 'text-secondary hover:bg-surface-alt'
                  : 'hover:bg-paper-cream'
              }`}
              style={isDiary ? diaryGroupStyle : undefined}
            >
              <div className="flex items-center gap-2">
                {isDiary && (
                  <span 
                    className="text-xs font-normal"
                    style={{ color: 'var(--ink-faded)', fontFamily: 'var(--font-body)' }}
                  >
                    {String(groupIndex + 1).padStart(2, '0')}.
                  </span>
                )}
                <group.icon className="h-4 w-4" />
                <span>{group.name}</span>
              </div>
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
            
            {/* Group Items - Page entries */}
            {isExpanded && (
              <div className={`mt-1 space-y-0.5 ${isDiary ? 'ml-4 pl-3 border-l-2' : 'ml-2'}`}
                style={isDiary ? { borderColor: 'var(--paper-margin)', borderStyle: 'dotted' } : undefined}
              >
                {group.items.map((item, itemIndex) => {
                  const isItemActive = isActive(item.href) ||
                    (item.href === '/schedule' && (isActive('/schedule') || isActive('/schedule-v2')))
                  
                  // Diary theme styles for nav items - Bookmark ribbon
                  const diaryItemStyle: React.CSSProperties = isItemActive
                    ? {
                        backgroundColor: 'var(--paper-cream)',
                        color: 'var(--ink-black)',
                        boxShadow: '2px 2px 6px var(--shadow-color, rgba(92, 64, 51, 0.1))',
                        position: 'relative',
                      }
                    : {
                        color: 'var(--ink-black)',
                      }
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={onItemClick}
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                        !isDiary
                          ? isItemActive
                            ? 'bg-brand text-on-brand shadow-md'
                            : 'text-primary hover:bg-surface-alt'
                          : 'hover:bg-paper-cream'
                      }`}
                      style={isDiary ? diaryItemStyle : undefined}
                    >
                      {/* Diary: Bookmark ribbon for active item */}
                      {isDiary && isItemActive && (
                        <div 
                          className="absolute -left-3 top-0 bottom-0 w-1"
                          style={{ 
                            backgroundColor: 'var(--bookmark-red)',
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                      )}
                      
                      {isDiary && (
                        <span 
                          className="text-xs mr-2 w-5"
                          style={{ 
                            color: isItemActive ? 'var(--ink-sepia)' : 'var(--ink-faded)',
                            fontFamily: 'var(--font-body)',
                          }}
                        >
                          {groupIndex + 1}.{itemIndex + 1}
                        </span>
                      )}
                      <item.icon className={`h-4 w-4 mr-3 ${isItemActive ? '' : 'opacity-70'}`} />
                      <span style={isDiary ? { fontFamily: 'var(--font-body)' } : undefined}>
                        {item.name}
                      </span>
                      {item.badge && (
                        <span
                          className={`ml-auto px-2 py-0.5 text-xs font-medium rounded-full ${
                            !isDiary ? 'bg-brand-subtle text-brand' : ''
                          }`}
                          style={isDiary ? {
                            backgroundColor: 'var(--washi-tape-pink)',
                            color: 'var(--ink-sepia)'
                          } : undefined}
                        >
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )

  // The diary theme keeps its book-cover header; the sky cap is the modern
  // shell's own thing.
  const renderDiaryHeader = (closeButton: React.ReactNode) => (
    <div
      className="flex flex-none items-center justify-between border-b px-4 py-4"
      style={diaryStyles.header}
    >
      <div className="flex min-w-0 items-center space-x-3">
        <img src={branding.icon} alt={branding.name} className="h-8 w-8 shrink-0 rounded-lg" />
        <div className="min-w-0">
          <h1
            className="truncate text-xl font-bold"
            style={{ color: 'var(--ink-sepia)', fontFamily: 'var(--font-handwritten)' }}
          >
            {branding.name}
          </h1>
          <p
            className="truncate text-app-nano italic"
            style={{ color: 'var(--ink-faded)', fontFamily: 'var(--font-body)' }}
          >
            {branding.tagline}
          </p>
        </div>
      </div>
      {closeButton}
    </div>
  )

  /** One rail, rendered twice: fixed on desktop, in a drawer on mobile. It
   *  used to be two near-identical 120-line copies that drifted apart. */
  const renderRail = (closeButton: React.ReactNode, onItemClick?: () => void) => (
    <>
      {isDiary ? renderDiaryHeader(closeButton) : renderSkyCap(closeButton)}
      {renderNavigation(onItemClick)}
      {renderFooter(onItemClick)}
    </>
  )

  /** The dismiss control sits inside the cap, so it has to be tinted by the
   *  sky rather than by the page: text-secondary is dark ink, which
   *  disappeared against the night gradient. --sky-lockup is the one colour
   *  guaranteed to be legible on the cap at every hour. */
  const capButton = (
    Icon: typeof ChevronLeft,
    onClick: () => void,
    label: string,
  ) => (
    <button
      onClick={onClick}
      aria-label={label}
      /* 2026-08-29: p-1.5 around a 20px icon is 32px — under the touch floor,
         and this is the only way to dismiss the mobile rail. */
      className={`inline-flex shrink-0 items-center justify-center min-h-touch min-w-touch rounded-md transition-colors ${
        !isDiary ? 'text-sky-lockup hover:bg-white/25' : 'text-secondary hover:bg-surface-alt'
      }`}
    >
      <Icon className="h-5 w-5" />
    </button>
  )

  const collapseButton = capButton(ChevronLeft, toggleSidebar, 'Collapse sidebar')
  const closeMobileButton = capButton(X, toggleMobileSidebar, 'Close menu')

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col ${
          sidebarOpen ? 'rail-width' : 'lg:w-0'
        }`}
      >
        <div className="app-rail flex flex-grow flex-col overflow-hidden border-r bg-surface border-soft">
          {renderRail(collapseButton)}
        </div>
      </div>
      
      {/* Mobile sidebar */}
      <div className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
        mobileSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        {/* Backdrop */}
        <div 
          className="fixed inset-0"
          style={{ backgroundColor: 'var(--overlay)' }}
          onClick={toggleMobileSidebar}
        />
        
        {/* Sidebar */}
        <div 
          className={`app-rail flex flex-col w-72 h-full border-r bg-surface border-soft transform transition-transform duration-300 ${
            mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {renderRail(closeMobileButton, toggleMobileSidebar)}
        </div>
      </div>
    </>
  )
}
